/**
 * @typedef {Object} WarehouseCreateDto
 * @property {string} warehouseCode
 * @property {string} warehouseName
 * @property {string} [address]
 */

/**
 * @typedef {Object} WarehouseUpdateDto
 * @property {string} [warehouseCode]
 * @property {string} [warehouseName]
 * @property {string} [address]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} WarehouseResponseDto
 * @property {number} warehouseId
 * @property {string} orgId
 * @property {string} warehouseCode
 * @property {string} warehouseName
 * @property {string} [address]
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 * @property {number} zoneCount
 */

/**
 * @typedef {Object} WarehouseListResponse
 * @property {WarehouseResponseDto[]} warehouses
 * @property {number} totalCount
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {number} totalPages
 */

/**
 * @typedef {Object} WarehouseZoneCreateDto
 * @property {string} zoneCode
 * @property {string} zoneName
 * @property {string} [description]
 */

/**
 * @typedef {Object} WarehouseZoneUpdateDto
 * @property {string} [zoneCode]
 * @property {string} [zoneName]
 * @property {string} [description]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} WarehouseZoneResponseDto
 * @property {number} zoneId
 * @property {number} warehouseId
 * @property {string} zoneCode
 * @property {string} zoneName
 * @property {string} [description]
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 * @property {number} locationCount
 */

/**
 * @typedef {Object} WarehouseLocationCreateDto
 * @property {string} locationCode
 * @property {string} locationName
 * @property {string} [aisle]
 * @property {string} [rack]
 * @property {string} [shelf]
 * @property {string} [bin]
 * @property {number} [capacity]
 */

/**
 * @typedef {Object} WarehouseLocationUpdateDto
 * @property {string} [locationCode]
 * @property {string} [locationName]
 * @property {string} [aisle]
 * @property {string} [rack]
 * @property {string} [shelf]
 * @property {string} [bin]
 * @property {number} [capacity]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} WarehouseLocationResponseDto
 * @property {number} locationId
 * @property {number} zoneId
 * @property {string} locationCode
 * @property {string} locationName
 * @property {string} [aisle]
 * @property {string} [rack]
 * @property {string} [shelf]
 * @property {string} [bin]
 * @property {number} [capacity]
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 */

const BASE_PATH = 'v1/Warehouse';

export default function (instance) {
  return {
    // Warehouse CRUD
    getWarehouses(params = {}) {
      return instance.get(BASE_PATH, { params });
    },

    getWarehouse(id) {
      return instance.get(`${BASE_PATH}/${id}`);
    },

    createWarehouse(payload) {
      return instance.post(BASE_PATH, payload);
    },

    updateWarehouse(id, payload) {
      return instance.put(`${BASE_PATH}/${id}`, payload);
    },

    deleteWarehouse(id) {
      return instance.delete(`${BASE_PATH}/${id}`);
    },

    // Zone CRUD
    getZones(warehouseId) {
      return instance.get(`${BASE_PATH}/${warehouseId}/zones`);
    },

    createZone(warehouseId, payload) {
      return instance.post(`${BASE_PATH}/${warehouseId}/zones`, payload);
    },

    updateZone(warehouseId, zoneId, payload) {
      return instance.put(`${BASE_PATH}/${warehouseId}/zones/${zoneId}`, payload);
    },

    deleteZone(warehouseId, zoneId) {
      return instance.delete(`${BASE_PATH}/${warehouseId}/zones/${zoneId}`);
    },

    // Location CRUD
    getLocations(warehouseId, zoneId) {
      return instance.get(`${BASE_PATH}/${warehouseId}/zones/${zoneId}/locations`);
    },

    createLocation(warehouseId, zoneId, payload) {
      return instance.post(`${BASE_PATH}/${warehouseId}/zones/${zoneId}/locations`, payload);
    },

    updateLocation(warehouseId, zoneId, locationId, payload) {
      return instance.put(`${BASE_PATH}/${warehouseId}/zones/${zoneId}/locations/${locationId}`, payload);
    },

    deleteLocation(warehouseId, zoneId, locationId) {
      return instance.delete(`${BASE_PATH}/${warehouseId}/zones/${zoneId}/locations/${locationId}`);
    }
  };
}
